import { create } from 'zustand';

/**
 * [Infra 1.5 #25] 店长面板的共享开合状态。面板遮罩在 DashboardLayout 挂一次，入口（FAB）与触发点
 * （任务中心失败行、Home AI Workspace 卡的失败状态行）在各页调 openPanel，失败行带 cardId 定位到卡。
 *
 * <p>只管「开没开、聚焦哪张」。卡片数据、红点计数、动作全走后端
 * （`recoveryCardApi`）：V0.9 删掉了 Later / Snoozed / Skip，卡只有两个去向 —— 主动作处理完，
 * 或 Delete task —— 两个都是后端状态，本地不再有 handled / snoozed 集合可言。
 */
interface FailurePanelState {
  open: boolean;
  focusCardId: string | null;
  /**
   * [infra1.5 验收 #1②] 从任务中心失败行打开时，是**哪一条任务**。
   *
   * <p>面板按它定位「它自己的卡」：合并卡里有这条任务就亮那张；没有就不亮。只带 cardId 时，
   * 服务端给错卡（实测 Maya 的失败行亮了 Oscar 的卡）前端没有任何办法发现。
   */
  focusTaskId: string | null;
  /**
   * 卡片集合的版本号 —— 面板每次把卡重新拉回来就 +1。
   *
   * <p>给**面板之外**、同样在展示这批卡的地方用（今天是简报的「Anything to watch」，#12）。
   * 光靠 `open` 不够：商家点了恢复动作、请求还在飞的时候就把面板关了，那一刻外面拉到的
   * 仍是没处理完的旧数据；动作随后成功，`open` 已经是 false 不会再变，那一行就一直挂着。
   * 面板的 refresh 落地后 bump 一次，外面才跟得上。
   *
   * <p>只是个信号，不带数据：卡片本身仍然只有一个来源（后端 `recoveryCardApi`），
   * 这里放一份副本就又成了两个地方各存一份。
   */
  cardsRevision: number;
  openPanel: (focusCardId?: string | null, focusTaskId?: string | null) => void;
  closePanel: () => void;
  bumpCards: () => void;
}

export const useFailurePanelStore = create<FailurePanelState>(set => ({
  open: false,
  focusCardId: null,
  focusTaskId: null,
  cardsRevision: 0,
  openPanel: (focusCardId = null, focusTaskId = null) =>
    set({ open: true, focusCardId, focusTaskId }),
  closePanel: () => set({ open: false, focusCardId: null, focusTaskId: null }),
  bumpCards: () => set(s => ({ cardsRevision: s.cardsRevision + 1 })),
}));
